"use client";

import React, { ReactNode, useEffect, useState } from 'react';
import { Overlay } from 'react-vant';
import styles from '@/styles/global/dialog.module.scss';
import { Button } from "react-vant";
import { Cross } from '@react-vant/icons';

interface DialogProps {
  visible: boolean;
  title?: string;
  children?: ReactNode;
  showConfirmButton?: boolean;
  showCancelButton?: boolean;
  confirmButtonText?: string;
  cancelButtonText?: string;
  onCancel?: () => void;
  onClose?: () => void;
  onConfirm?: () => void;
  className?: string;
  titleClassName?: string;
  actionClassName?: string;
  confirmButtonClassName?: string;
  cancelButtonClassName?: string;
  confirmButtonStyle?: React.CSSProperties;
  cancelButtonStyle?: React.CSSProperties;
  confirmLoading?: boolean;
  closeable?: boolean;
}

const Dialog: React.FC<DialogProps> = ({
  visible,
  title,
  children,
  showConfirmButton = true,
  showCancelButton = true,
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  onCancel,
  onClose,
  onConfirm,
  className,
  titleClassName,
  actionClassName,
  confirmButtonClassName,
  cancelButtonClassName,
  confirmButtonStyle,
  cancelButtonStyle,
  confirmLoading,
  closeable = false
}) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!mounted) return null;

  return (
    <Overlay
      visible={visible}
      onClick={onClose || onCancel}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
      className={className}
      zIndex={9}
    >
      <div 
        className={styles['dialog-content']}
        onClick={(e) => e.stopPropagation()}
        style={{ position: 'relative' }}
      >
        {closeable && (
          <div 
            className={styles['dialog-close']}
            onClick={onClose || onCancel}
          >
            <Cross fontSize={22} color='#666666' />
          </div>
        )}

        {
          title ? (
            <div className={`${styles['dialog-title']} ${titleClassName || ''}`}>{title}</div>
          ) : null
        }

        <div className={styles['dialog-body']}>
          {children}
        </div>
        {(showConfirmButton || showCancelButton) && (
          <div 
            className={`${styles['dialog-actions']} ${actionClassName || ''}`}
          >
            {showCancelButton && (
              <Button
                block
                className={`${styles['dialog-cancel-button']} ${cancelButtonClassName || ''}`}
                style={{ 
                  flex: 1,
                  marginRight: showConfirmButton ? '8px' : 0,
                  ...cancelButtonStyle
                }}
                onClick={onCancel}
              >
                {cancelButtonText}
              </Button>
            )}
            {showConfirmButton && (
              <Button
                type="primary"
                block
                className={`${styles['dialog-confirm-button']} ${confirmButtonClassName || ''}`}
                style={{ 
                  flex: 1,
                  marginLeft: showCancelButton ? '8px' : 0,
                  ...confirmButtonStyle
                }}
                loading={confirmLoading}
                loadingText={confirmButtonText}
                onClick={onConfirm}
              >
                {confirmButtonText}
              </Button>
            )}
          </div>
        )}
      </div>
    </Overlay>
  );
};

export default Dialog;
